import { AccountContactType } from "../../ConfirmationStep/Signature/signature.interface";
import { Contract } from "../../contract.interface";
import { SubmitOwnershipTransferFieldsValues } from "../fields/ownership-transfer-fields.interface";

import { UseGetTermDataDraftResponse } from "@/core/libs/api/react-query/hook/use-get-term-data-draft/use-get-term-data-draft.types";
import { ExecuteData } from "@/core/libs/api/react-query/useApi";
import { FetchResponse } from "@/core/libs/api/utils/fetchData";
import { AccountType } from "@/modules/Accounts/types";
import { TermTypeEnum } from "@/modules/Terms/interfaces/ITerm.interface";
import { normalizeDocumentNumber } from "@/utils/NormalizeDocumentNumber";

interface SubmitTermDataDraftParams {
  contract: Contract;
  termDataDraft: UseGetTermDataDraftResponse;
  execute: (data: ExecuteData) => Promise<FetchResponse>;
  setIsSubmitting: (value: boolean) => void;
  setCanAdvance: (value: boolean) => void;
  isCustomForm: boolean;
  values?: SubmitOwnershipTransferFieldsValues;
  assignorAccountType?: AccountType;
  setIsDrawerOpen?: (value: boolean) => void;
  setErrorMessage: (message: string) => void;
}

const sanitizeNumber = (value?: string) => value?.replace(/\D/g, "");
const sanitizeDocument = (value?: string) =>
  value?.replace(/[^\p{L}\p{N}]/gu, "");

const buildCustomFormBody = (
  contract: Contract,
  values: SubmitOwnershipTransferFieldsValues,
  assignorAccountType?: AccountType,
  termId?: number
) => {
  const address = values.addresses?.[0];

  return {
    fullName: values.fullName,
    email: values.email,
    whatsapp: sanitizeNumber(values.whatsapp),
    documentNumber: normalizeDocumentNumber(values.documentNumber),
    secondaryDocument: sanitizeDocument(values.secondaryDocument),
    legalRepresentativeName: values.legalRepresentativeName,
    legalRepresentativeDocument: sanitizeNumber(
      values.legalRepresentativeDocument
    ),
    accountType: assignorAccountType,
    street: address?.street,
    number: address?.number,
    neighborhood: address?.neighborhood,
    city: address?.city,
    uf: address?.state,
    zipCode: address?.zipCode,
    contractId: contract.id,
    note: address?.note,
    id: termId,
    termType: TermTypeEnum.CHANGE_OF_OWNERSHIP
  };
};

const buildDefaultBody = (contract: Contract, termId?: number) => {
  const { account } = contract;
  const address = account.addresses?.[0];
  const document = account.documents?.[0];

  const whatsapp = account.contacts.find(
    contact => contact.contactType === AccountContactType.PHONE_NUMBER
  )?.contact;

  const email = account.contacts.find(
    contact => contact.contactType === AccountContactType.EMAIL
  )?.contact;

  return {
    fullName: account.fullName,
    legalRepresentativeName: account.legalRepresentatives?.[0]?.fullName,
    legalRepresentativeDocument: account.legalRepresentatives?.[0]?.document,
    secondaryDocument: account.secondaryDocument?.documentNumber,
    documentNumber: document?.documentNumber,
    documentType: account.accountType,
    termType: TermTypeEnum.CHANGE_OF_OWNERSHIP,
    accountType: account.accountType,
    whatsapp,
    email,
    street: address?.street,
    number: address?.number,
    neighborhood: address?.neighborhood,
    city: address?.city,
    uf: address?.state,
    zipCode: address?.zipCode,
    contractId: contract.id,
    note: address?.note,
    id: termId
  };
};

export const submitTermDataDraft = async ({
  contract,
  termDataDraft,
  execute,
  setCanAdvance,
  isCustomForm,
  setIsSubmitting,
  values,
  assignorAccountType,
  setIsDrawerOpen,
  setErrorMessage
}: SubmitTermDataDraftParams) => {
  const termId = termDataDraft?.list?.[0]?.id;
  const bodyObj =
    isCustomForm && values
      ? buildCustomFormBody(contract, values, assignorAccountType, termId)
      : buildDefaultBody(contract, termId);

  setIsSubmitting(true);
  setErrorMessage(undefined);

  const request = await execute({
    url: `term-data-draft`,
    method: "POST",
    bodyObj
  });

  if (request?.response?.ok) {
    termDataDraft.invalidateQuery();
    setCanAdvance(true);

    if (setIsDrawerOpen) setIsDrawerOpen(false);
  } else {
    const errorMessage =
      request?.error?.message || "Erro ao enviar os dados do termo.";
    setErrorMessage(errorMessage);
  }

  setIsSubmitting(false);
};
